import type { ConversationMessage, ToolDefinition, ToolMessage } from './types';
import { isAssistantMessage, isSystemMessage, isToolMessage, isUserMessage } from './types';

const CHARS_PER_TOKEN = 4;
const MESSAGE_OVERHEAD_TOKENS = 4;
const IMAGE_PART_TOKENS = 1000;

/**
 * Estimate token count for a single message.
 * Uses a simple chars/4 heuristic plus overhead per message.
 */
export function estimateMessageTokens(msg: ConversationMessage): number {
    let contentTokens: number;

    if (typeof msg.content === 'string') {
        contentTokens = Math.ceil(msg.content.length / CHARS_PER_TOKEN);
    } else {
        contentTokens = msg.content.reduce((sum, part) => {
            if (part.type === 'text') {
                return sum + Math.ceil(part.text.length / CHARS_PER_TOKEN);
            }
            // Image parts
            return sum + IMAGE_PART_TOKENS;
        }, 0);
    }

    // Assistant messages with tool_calls consume tokens for function names, arguments, and IDs
    if (isAssistantMessage(msg) && msg.tool_calls) {
        contentTokens += Math.ceil(JSON.stringify(msg.tool_calls).length / CHARS_PER_TOKEN);
    }

    return contentTokens + MESSAGE_OVERHEAD_TOKENS;
}

/**
 * Estimate the token count of a raw string, using the same chars/token heuristic as message
 * estimation (without the per-message overhead). Handy for sizing a single piece of content before
 * it becomes a message.
 */
export function estimateTextTokens(text: string): number {
    return Math.ceil(text.length / CHARS_PER_TOKEN);
}

/**
 * Once a single message takes this fraction of the model's context window on its own it is large
 * enough to crowd out the rest of the conversation (and, past the window, wedge it entirely). Used to
 * flag an outsized video-state message, whose size is no longer bounded by field truncation.
 */
export const OVERSIZED_CONTEXT_MESSAGE_FRACTION = 0.5;

/**
 * Whether a single message's content is large enough, relative to the model's context limit, to be
 * treated as oversized. Used to flag an outsized non-evictable video-state message before it starts
 * degrading or failing requests.
 */
export function isContextMessageOversized(
    text: string,
    contextLimit: number,
    fraction: number = OVERSIZED_CONTEXT_MESSAGE_FRACTION,
): boolean {
    return estimateTextTokens(text) >= contextLimit * fraction;
}

/**
 * Estimate token count for tool definitions (function schemas).
 */
export function estimateToolDefinitionsTokens(tools?: ToolDefinition[]): number {
    if (!tools || tools.length === 0) {
        return 0;
    }
    return Math.ceil(JSON.stringify(tools).length / CHARS_PER_TOKEN);
}

/**
 * Estimate total token count for a set of messages and tool definitions.
 */
export function estimateTotalTokens(
    messages: ConversationMessage[],
    tools?: ToolDefinition[],
): number {
    const messageTokens = messages.reduce((sum, msg) => sum + estimateMessageTokens(msg), 0);
    return messageTokens + estimateToolDefinitionsTokens(tools);
}

interface TruncationResult {
    messages: ConversationMessage[];
    removedCount: number;
    originalCount: number;
    estimatedTokensBefore: number;
    estimatedTokensAfter: number;
    // True when the non-removable messages (system, latest context/user message) plus tools
    // exceed the limit on their own, so truncation can't bring the request under it.
    stillOverLimit: boolean;
}

/**
 * Truncates messages to fit within a context token limit.
 * Groups tool-call assistant messages with their corresponding tool result messages
 * so they are always removed atomically. System messages, the latest context message,
 * and the most recent user message are never removed.
 */
export function truncateMessagesToFitContext(
    messages: ConversationMessage[],
    contextLimit: number,
    tools?: ToolDefinition[],
): TruncationResult {
    const totalTokens = estimateTotalTokens(messages, tools);

    if (totalTokens <= contextLimit) {
        return {
            messages,
            removedCount: 0,
            originalCount: messages.length,
            estimatedTokensBefore: totalTokens,
            estimatedTokensAfter: totalTokens,
            stillOverLimit: false,
        };
    }

    type MessageGroup = {
        messages: ConversationMessage[];
        removable: boolean;
    };

    const groups: MessageGroup[] = [];
    const toolCallIdToGroupIndex = new Map<string, number>();

    for (const msg of messages) {
        if (isSystemMessage(msg)) {
            groups.push({ messages: [msg], removable: false });
        } else if (isAssistantMessage(msg) && msg.tool_calls && msg.tool_calls.length > 0) {
            const groupIndex = groups.length;
            groups.push({ messages: [msg], removable: true });
            for (const tc of msg.tool_calls) {
                toolCallIdToGroupIndex.set(tc.id, groupIndex);
            }
        } else if (
            isToolMessage(msg) &&
            toolCallIdToGroupIndex.has((msg as ToolMessage).tool_call_id)
        ) {
            const groupIndex = toolCallIdToGroupIndex.get((msg as ToolMessage).tool_call_id)!;
            groups[groupIndex].messages.push(msg);
        } else {
            groups.push({ messages: [msg], removable: true });
        }
    }

    // Mark the last context message as non-removable
    for (let i = groups.length - 1; i >= 0; i--) {
        if (groups[i].messages.some((m) => m.isContextMessage)) {
            groups[i].removable = false;
            break;
        }
    }

    // Mark the most recent user message group as non-removable
    for (let i = groups.length - 1; i >= 0; i--) {
        if (groups[i].messages.some((m) => isUserMessage(m))) {
            groups[i].removable = false;
            break;
        }
    }

    // Remove oldest removable groups until under the limit
    let currentTokens = totalTokens;
    let removedCount = 0;
    for (let i = 0; i < groups.length && currentTokens > contextLimit; i++) {
        if (!groups[i].removable) {
            continue;
        }
        for (const msg of groups[i].messages) {
            currentTokens -= estimateMessageTokens(msg);
            removedCount++;
        }
        groups[i].messages = [];
    }

    return {
        messages: groups.flatMap((g) => g.messages),
        removedCount,
        originalCount: messages.length,
        estimatedTokensBefore: totalTokens,
        estimatedTokensAfter: currentTokens,
        stillOverLimit: currentTokens > contextLimit,
    };
}
